import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TabModel } from '../../types';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  template: '',
  standalone: true,
})
export class BaseTabsComponent implements OnInit, OnChanges {
  protected readonly router = inject(Router);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly cd = inject(ChangeDetectorRef);

  selectedTabIndex = 0;

  @Input()
  tabs: TabModel[] = [];

  @Input()
  selectTabById: number | string;

  /* It gets called when a tab is changed and returns an index of the tab */
  @Output()
  tabChange = new EventEmitter<number | string | any>();

  /* It gets called only when a tab is manually clicked and returns an indexes of previous and current tab */
  @Output()
  tabClick = new EventEmitter<{ previousTab: number; currentTab: number }>();

  get hasTabs(): boolean {
    return !!this.tabs?.length;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('tabs' in changes && this.tabs) {
      this.syncTabsWithRouter();
    }

    if ('selectTabById' in changes && this.selectTabById && this.tabs) {
      const index = this.tabs.indexOf(this.tabs.find((tab) => tab.tabId === this.selectTabById));
      const disableCondition = this.tabs[index]?.disableCondition;
      let disabled: boolean;

      if (disableCondition) {
        disabled = disableCondition();
      }

      if (!disabled) {
        this.selectedTabIndex = index;
      }
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        debounceTime(400),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncTabsWithRouter());
  }

  selectTab(index: number, shouldEmitClick = false): void {
    if (this.selectedTabIndex !== index) {
      if (shouldEmitClick) {
        this.tabClick.emit({ previousTab: this.selectedTabIndex, currentTab: index });
      }

      this.tabChange.emit(this.tabs[index].tabId);
    }
    this.selectedTabIndex = index;
    this.cd.detectChanges();
  }

  protected syncTabsWithRouter(): void {
    const activeTabIndex = this.tabs.findIndex(
      (tab) =>
        tab.routerLink &&
        this.router.isActive(this.handleLink(tab.routerLink), {
          paths: !tab.routerPathHandling ? 'exact' : tab.routerPathHandling,
          queryParams: 'subset',
          matrixParams: 'exact',
          fragment: 'exact',
        })
    );

    if (activeTabIndex >= 0) {
      this.selectTab(activeTabIndex);
      this.tabChange.emit(this.tabs[activeTabIndex].tabId);
    }
  }

  private handleLink(link: string): string {
    return link.replace(/\(/g, '%28').replace(/\)/g, '%29');
  }
}
